import { apiClient } from '@/api/client';
import {
  PathsAdminUsersUserIdPlansGetParametersQueryStatus,
  Plan,
} from '@/api/models';
import { queryKeys } from '@/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

export interface UserPlansFilters {
  page: number;
  limit: number;
  status?: PathsAdminUsersUserIdPlansGetParametersQueryStatus;
  sortBy: 'startAt' | 'endAt' | 'createdAt' | 'status';
  sortOrder: 'asc' | 'desc';
}

export interface UserPlansState {
  plans: Plan[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  filters: UserPlansFilters;
}

export interface UserPlansActions {
  setPage: (page: number) => void;
  setStatus: (
    status: PathsAdminUsersUserIdPlansGetParametersQueryStatus | undefined,
  ) => void;
  setSortBy: (sortBy: UserPlansFilters['sortBy']) => void;
  setSortOrder: (sortOrder: 'asc' | 'desc') => void;
  resetFilters: () => void;
}

const defaultFilters: UserPlansFilters = {
  page: 1,
  limit: 10,
  status: undefined,
  sortBy: 'startAt',
  sortOrder: 'desc',
};

export const useUserPlans = (userId: number | undefined) => {
  const [filters, setFilters] =
    React.useState<UserPlansFilters>(defaultFilters);

  // Reset filters when userId changes
  React.useEffect(() => {
    setFilters(defaultFilters);
  }, [userId]);

  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.ADMIN_USER_PLANS, userId, filters],
    queryFn: async () => {
      if (!userId) return { plans: [], total: 0, totalPages: 0 };

      const response = await apiClient.GET('/admin/users/{userId}/plans', {
        params: {
          path: { userId },
          query: {
            page: filters.page,
            limit: filters.limit,
            status: filters.status,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          },
        },
      });

      if (response.error) {
        throw new Error('Failed to fetch user plans');
      }

      // Get pagination info from headers
      const total = parseInt(
        response.response.headers.get('X-Total-Count') || '0',
      );
      const totalPages = parseInt(
        response.response.headers.get('X-Total-Pages') || '0',
      );

      return {
        plans: response.data || [],
        total,
        totalPages,
      };
    },
    enabled: !!userId,
  });

  const setPage = React.useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const setStatus = React.useCallback(
    (
      status: PathsAdminUsersUserIdPlansGetParametersQueryStatus | undefined,
    ) => {
      setFilters(prev => ({ ...prev, status, page: 1 }));
    },
    [],
  );

  const setSortBy = React.useCallback((sortBy: UserPlansFilters['sortBy']) => {
    setFilters(prev => ({ ...prev, sortBy, page: 1 }));
  }, []);

  const setSortOrder = React.useCallback((sortOrder: 'asc' | 'desc') => {
    setFilters(prev => ({ ...prev, sortOrder, page: 1 }));
  }, []);

  const resetFilters = React.useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const state: UserPlansState = {
    plans: data?.plans || [],
    total: data?.total || 0,
    totalPages: data?.totalPages || 0,
    isLoading,
    filters,
  };

  const actions: UserPlansActions = {
    setPage,
    setStatus,
    setSortBy,
    setSortOrder,
    resetFilters,
  };

  return { state, actions };
};
